import { useState, useEffect, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
    DialogDescription,
} from '../components/ui/dialog';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
    Link2,
    Trash2,
    MoreVertical,
    ExternalLink,
    Loader2,
    AlertCircle,
    CheckCircle,
    Settings,
    Info,
    Key,
    LogIn,
    RefreshCw
} from 'lucide-react';
import { cn } from '../lib/utils';
import { toast } from 'sonner';
import { format } from 'date-fns';
import SessionLoginModal from '../components/SessionLoginModal';

const PLATFORMS = [
    { id: 'instagram', name: 'Instagram', emoji: '📸', color: 'bg-gradient-to-r from-purple-500 to-pink-500', authType: 'oauth' },
    { id: 'facebook', name: 'Facebook', emoji: '👤', color: 'bg-blue-600', authType: 'oauth' },
    { id: 'twitter', name: 'Twitter/X', emoji: '🐦', color: 'bg-sky-500', authType: 'oauth' },
    { id: 'linkedin', name: 'LinkedIn', emoji: '💼', color: 'bg-blue-700', authType: 'oauth' },
    { id: 'tiktok', name: 'TikTok', emoji: '🎵', color: 'bg-black', authType: 'oauth' },
    { id: 'pinterest', name: 'Pinterest', emoji: '📌', color: 'bg-red-600', authType: 'oauth' },
    { id: 'youtube', name: 'YouTube', emoji: '🎬', color: 'bg-red-500', authType: 'oauth' },
];

// Platforms that use direct session login (no API keys needed)
const SESSION_AUTH_PLATFORMS = [];

export default function Accounts() {
    const { api, token, user } = useAuth();
    const [searchParams, setSearchParams] = useSearchParams();
    const navigate = useNavigate();
    const [accounts, setAccounts] = useState([]);
    const [platformStatus, setPlatformStatus] = useState([]);
    const [loading, setLoading] = useState(true);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [accountToDelete, setAccountToDelete] = useState(null);
    const [connecting, setConnecting] = useState(null);
    const [setupDialogOpen, setSetupDialogOpen] = useState(false);
    const [selectedPlatform, setSelectedPlatform] = useState(null);
    const [sessionLoginPlatform, setSessionLoginPlatform] = useState(null);

    const fetchData = useCallback(async () => {
        setLoading(true);
        try {
            const [accountsRes, platformsRes] = await Promise.all([
                api.get('/social-accounts'),
                api.get('/oauth/platforms')
            ]);
            setAccounts(accountsRes.data);
            setPlatformStatus(platformsRes.data);
        } catch (error) {
            console.error('Failed to fetch data:', error);
            toast.error('Failed to load accounts');
        } finally {
            setLoading(false);
        }
    }, [api]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    // Handle OAuth callback
    useEffect(() => {
        const handleOAuthCallback = async () => {
            const oauthSuccess = searchParams.get('oauth_success');
            const error = searchParams.get('error');

            if (error) {
                toast.error(`Connection failed: ${error.replace(/_/g, ' ')}`);
                setSearchParams({});
                return;
            }

            if (oauthSuccess) {
                try {
                    const base64 = oauthSuccess.replace(/-/g, '+').replace(/_/g, '/');
                    const data = JSON.parse(atob(base64));
                    
                    // Save the connected account
                    await api.post('/social-accounts', {
                        platform: data.platform,
                        access_token: data.access_token,
                        refresh_token: data.refresh_token,
                        account_id: data.account_id,
                        account_name: data.account_name,
                        profile_picture: data.profile_picture,
                        expires_at: data.expires_in 
                            ? new Date(Date.now() + data.expires_in * 1000).toISOString()
                            : null
                    });
                    
                    toast.success(`${data.platform.charAt(0).toUpperCase() + data.platform.slice(1)} connected successfully!`);
                    fetchData();
                } catch (err) {
                    console.error('Failed to save account:', err);
                    toast.error('Failed to save connected account');
                }
                setSearchParams({});
            }
        };

        handleOAuthCallback();
    }, [searchParams, setSearchParams, api, fetchData]);

    const handleConnect = (platform) => {
        // Session-based login (Playwright) — currently disabled; all platforms use OAuth
        if (SESSION_AUTH_PLATFORMS.includes(platform)) {
            setSessionLoginPlatform(platform);
            return;
        }
        
        // Use OAuth for other platforms
        const status = platformStatus.find(p => p.platform === platform);
        
        if (!status?.configured) {
            setSelectedPlatform(PLATFORMS.find(p => p.id === platform));
            setSetupDialogOpen(true);
            return;
        }

        setConnecting(platform);
        
        // Redirect to OAuth flow
        const backendUrl = process.env.REACT_APP_BACKEND_URL;
        window.location.href = `${backendUrl}/api/oauth/connect/${platform}?user_token=${token}`;
    };

    const handleSessionLoginSuccess = () => {
        fetchData();
        setSessionLoginPlatform(null);
    };

    const handleDisconnect = async () => {
        if (!accountToDelete) return;
        try {
            await api.delete(`/social-accounts/${accountToDelete.id}`);
            toast.success('Account disconnected');
            setAccounts(prev => prev.filter(a => a.id !== accountToDelete.id));
            setDeleteDialogOpen(false);
            setAccountToDelete(null);
        } catch (error) {
            console.error('Failed to disconnect account:', error);
            toast.error('Failed to disconnect account');
        }
    };

    const handleVerifyConnection = async (account) => {
        try {
            const res = await api.get(`/social-accounts/${account.id}/check-ig`);
            if (res.data.status === 'found') {
                toast.success(`Verified! Connected to @${res.data.ig_account.username}`);
                fetchData();
            } else if (res.data.status === 'not_ready') {
                toast.info(res.data.message);
            } else {
                toast.warning(res.data.message || 'Unable to verify connection');
            }
        } catch (error) {
            const message = error.response?.data?.detail || 'Failed to verify connection';
            toast.error(message);
        }
    };

    const getPlatformInfo = (platformId) => {
        return PLATFORMS.find(p => p.id === platformId) || {
            id: platformId,
            name: platformId,
            emoji: '📱',
            color: 'bg-gray-500'
        };
    };

    const isConfigured = (platformId) => {
        // Session auth platforms are always "ready" - no API keys needed
        if (SESSION_AUTH_PLATFORMS.includes(platformId)) {
            return true;
        }
        return platformStatus.find(p => p.platform === platformId)?.configured || false;
    };

    const getAccountsForPlatform = (platformId) => {
        return accounts.filter(a => a.platform === platformId);
    };

    return (
        <div className="space-y-6 animate-fade-in" data-testid="accounts-page">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                    <h1 className="text-3xl font-bold">Connected Accounts</h1>
                    <p className="text-muted-foreground mt-1">
                        Connect your social media accounts to start scheduling
                    </p>
                </div>
            </div>

            {loading ? (
                <div className="flex items-center justify-center h-[400px]">
                    <Loader2 className="h-8 w-8 animate-spin text-primary" />
                </div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {PLATFORMS.map(platform => {
                        const isSessionAuth = SESSION_AUTH_PLATFORMS.includes(platform.id);
                        const configured = isConfigured(platform.id);
                        const platformAccounts = getAccountsForPlatform(platform.id);
                        
                        return (
                            <Card 
                                key={platform.id} 
                                className="hover-lift overflow-hidden"
                                data-testid={`platform-card-${platform.id}`}
                            >
                                <div className={cn("h-2", platform.color)} />
                                <CardHeader className="pb-3">
                                    <div className="flex items-center justify-between">
                                        <div className="flex items-center gap-3">
                                            <span className="text-2xl">{platform.emoji}</span>
                                            <div>
                                                <CardTitle className="text-lg">{platform.name}</CardTitle>
                                                <CardDescription className="text-xs flex items-center gap-1">
                                                    {isSessionAuth ? (
                                                        <span className="text-green-500 flex items-center gap-1">
                                                            <LogIn className="h-3 w-3" />
                                                            Direct Login
                                                        </span>
                                                    ) : !configured ? (
                                                        <span className="text-yellow-500 flex items-center gap-1">
                                                            <AlertCircle className="h-3 w-3" />
                                                            Setup required
                                                        </span>
                                                    ) : (
                                                        <span className="text-muted-foreground flex items-center gap-1">
                                                            <Key className="h-3 w-3" />
                                                            OAuth
                                                        </span>
                                                    )}
                                                </CardDescription>
                                            </div>
                                        </div>
                                        <Badge variant={platformAccounts.length > 0 ? "default" : "secondary"}>
                                            {platformAccounts.length} connected
                                        </Badge>
                                    </div>
                                </CardHeader>
                                <CardContent className="space-y-3">
                                    {platformAccounts.length > 0 ? (
                                        <div className="space-y-2">
                                            {platformAccounts.map(account => (
                                                <div
                                                    key={account.id}
                                                    className="flex items-center justify-between p-3 rounded-lg bg-muted/50"
                                                    data-testid={`account-${account.id}`}
                                                >
                                                    <div className="flex items-center gap-3">
                                                        <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center overflow-hidden">
                                                            {account.profile_picture ? (
                                                                <img
                                                                    src={account.profile_picture}
                                                                    alt={account.account_name}
                                                                    className="w-full h-full object-cover"
                                                                />
                                                            ) : (
                                                                <span className="text-lg">{platform.emoji}</span>
                                                            )}
                                                        </div>
                                                        <div>
                                                            <p className="font-medium text-sm">{account.account_name}</p>
                                                            <div className="flex items-center gap-1 text-xs text-muted-foreground">
                                                                <CheckCircle className="h-3 w-3 text-green-500" />
                                                                <span>Connected</span>
                                                            </div>
                                                        </div>
                                                    </div>
                                                    <DropdownMenu>
                                                        <DropdownMenuTrigger asChild>
                                                            <Button variant="ghost" size="icon">
                                                                <MoreVertical className="h-4 w-4" />
                                                            </Button>
                                                        </DropdownMenuTrigger>
                                                        <DropdownMenuContent align="end">
                                                            {account.platform === 'instagram' && (
                                                                <DropdownMenuItem
                                                                    onClick={() => handleVerifyConnection(account)}
                                                                >
                                                                    <RefreshCw className="mr-2 h-4 w-4" />
                                                                    Verify Connection
                                                                </DropdownMenuItem>
                                                            )}
                                                            <DropdownMenuItem
                                                                className="text-destructive focus:text-destructive"
                                                                onClick={() => {
                                                                    setAccountToDelete(account);
                                                                    setDeleteDialogOpen(true);
                                                                }}
                                                            >
                                                                <Trash2 className="mr-2 h-4 w-4" />
                                                                Disconnect
                                                            </DropdownMenuItem>
                                                        </DropdownMenuContent>
                                                    </DropdownMenu>
                                                </div>
                                            ))}
                                        </div>
                                    ) : null}
                                    
                                    <Button
                                        className={cn(
                                            "w-full",
                                            isSessionAuth
                                                ? `bg-gradient-to-r ${platform.color} hover:opacity-90`
                                                : configured 
                                                    ? "gradient-bg" 
                                                    : "bg-muted text-muted-foreground hover:bg-muted/80"
                                        )}
                                        onClick={() => handleConnect(platform.id)}
                                        disabled={connecting === platform.id}
                                        data-testid={`connect-${platform.id}-btn`}
                                    >
                                        {connecting === platform.id ? (
                                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                        ) : isSessionAuth ? (
                                            <LogIn className="mr-2 h-4 w-4" />
                                        ) : configured ? (
                                            <Link2 className="mr-2 h-4 w-4" />
                                        ) : (
                                            <Settings className="mr-2 h-4 w-4" />
                                        )}
                                        {isSessionAuth
                                            ? `Sign in to ${platform.name}`
                                            : configured 
                                                ? `Connect ${platform.name}` 
                                                : `Setup ${platform.name}`
                                        }
                                    </Button>
                                </CardContent>
                            </Card>
                        );
                    })}
                </div>
            )}

            {/* Session Login Modal for Instagram and TikTok */}
            {sessionLoginPlatform && (
                <SessionLoginModal
                    platform={sessionLoginPlatform}
                    isOpen={!!sessionLoginPlatform}
                    onClose={() => setSessionLoginPlatform(null)}
                    onSuccess={handleSessionLoginSuccess}
                    api={api}
                />
            )}

            {/* Setup Required Dialog */}
            <Dialog open={setupDialogOpen} onOpenChange={setSetupDialogOpen}>
                <DialogContent className="max-w-lg">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <span className="text-2xl">{selectedPlatform?.emoji}</span>
                            Setup {selectedPlatform?.name}
                        </DialogTitle>
                        <DialogDescription>
                            To connect {selectedPlatform?.name} accounts, you need to configure API credentials.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                        <div className="p-4 rounded-lg bg-muted/50 border border-border">
                            <h4 className="font-medium mb-2 flex items-center gap-2">
                                <Info className="h-4 w-4 text-primary" />
                                How to configure
                            </h4>
                            <ol className="text-sm text-muted-foreground space-y-2 list-decimal list-inside">
                                <li>Go to <strong>Settings → OAuth Config</strong></li>
                                <li>Click "Configure" on {selectedPlatform?.name}</li>
                                <li>Get credentials from the {selectedPlatform?.name} Developer Portal</li>
                                <li>Enter your Client ID and Client Secret</li>
                                <li>Click Save - you're ready to connect!</li>
                            </ol>
                        </div>
                        
                        <div className="space-y-2">
                            <p className="text-sm font-medium">Developer Portal Links:</p>
                            <div className="grid grid-cols-1 gap-2">
                                {selectedPlatform?.id === 'instagram' || selectedPlatform?.id === 'facebook' ? (
                                    <a 
                                        href="https://developers.facebook.com" 
                                        target="_blank" 
                                        rel="noopener noreferrer"
                                        className="flex items-center gap-2 text-sm text-primary hover:underline"
                                    >
                                        <ExternalLink className="h-4 w-4" />
                                        Meta for Developers
                                    </a>
                                ) : selectedPlatform?.id === 'twitter' ? (
                                    <a 
                                        href="https://developer.twitter.com" 
                                        target="_blank" 
                                        rel="noopener noreferrer"
                                        className="flex items-center gap-2 text-sm text-primary hover:underline"
                                    >
                                        <ExternalLink className="h-4 w-4" />
                                        Twitter Developer Portal
                                    </a>
                                ) : selectedPlatform?.id === 'linkedin' ? (
                                    <a 
                                        href="https://www.linkedin.com/developers" 
                                        target="_blank" 
                                        rel="noopener noreferrer"
                                        className="flex items-center gap-2 text-sm text-primary hover:underline"
                                    >
                                        <ExternalLink className="h-4 w-4" />
                                        LinkedIn Developers
                                    </a>
                                ) : selectedPlatform?.id === 'tiktok' ? (
                                    <a 
                                        href="https://developers.tiktok.com" 
                                        target="_blank" 
                                        rel="noopener noreferrer"
                                        className="flex items-center gap-2 text-sm text-primary hover:underline"
                                    >
                                        <ExternalLink className="h-4 w-4" />
                                        TikTok for Developers
                                    </a>
                                ) : selectedPlatform?.id === 'pinterest' ? (
                                    <a 
                                        href="https://developers.pinterest.com" 
                                        target="_blank" 
                                        rel="noopener noreferrer"
                                        className="flex items-center gap-2 text-sm text-primary hover:underline"
                                    >
                                        <ExternalLink className="h-4 w-4" />
                                        Pinterest Developers
                                    </a>
                                ) : selectedPlatform?.id === 'youtube' ? (
                                    <a 
                                        href="https://console.cloud.google.com" 
                                        target="_blank" 
                                        rel="noopener noreferrer"
                                        className="flex items-center gap-2 text-sm text-primary hover:underline"
                                    >
                                        <ExternalLink className="h-4 w-4" />
                                        Google Cloud Console
                                    </a>
                                ) : null}
                            </div>
                        </div>

                        <div className="p-3 rounded-lg bg-primary/10 border border-primary/20">
                            <p className="text-sm">
                                <strong>Callback URL to use:</strong>
                            </p>
                            <code className="text-xs text-primary break-all">
                                {process.env.REACT_APP_BACKEND_URL}/api/oauth/callback/{selectedPlatform?.id}
                            </code>
                        </div>
                    </div>
                    <DialogFooter className="flex-col sm:flex-row gap-2">
                        <Button variant="outline" onClick={() => setSetupDialogOpen(false)}>
                            Close
                        </Button>
                        {user?.role === 'admin' && (
                            <Button onClick={() => {
                                setSetupDialogOpen(false);
                                navigate('/settings?tab=oauth');
                            }}>
                                <Key className="mr-2 h-4 w-4" />
                                Go to OAuth Config
                            </Button>
                        )}
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Disconnect Confirmation Dialog */}
            <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Disconnect Account</DialogTitle>
                    </DialogHeader>
                    <p>
                        Are you sure you want to disconnect "{accountToDelete?.account_name}"?
                    </p>
                    <p className="text-sm text-muted-foreground">
                        You won't be able to schedule posts to this account until you reconnect it.
                    </p>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>
                            Cancel
                        </Button>
                        <Button variant="destructive" onClick={handleDisconnect}>
                            Disconnect
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
